import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { TrendingUp, DollarSign, Package, Activity } from 'lucide-react';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FinancialReportsPage = () => {
  const [incomeStatement, setIncomeStatement] = useState(null);
  const [balanceSheet, setBalanceSheet] = useState(null);
  const [cashFlow, setCashFlow] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('income');

  useEffect(() => {
    fetchReports();
  }, []);

  const fetchReports = async () => {
    try {
      const [income, balance, cash] = await Promise.all([
        axios.get(`${API_URL}/reports/income-statement`),
        axios.get(`${API_URL}/reports/balance-sheet`),
        axios.get(`${API_URL}/reports/cash-flow`)
      ]);
      setIncomeStatement(income.data);
      setBalanceSheet(balance.data);
      setCashFlow(cash.data);
    } catch (error) {
      toast.error('Failed to load financial reports');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center h-64"><div className="text-muted-foreground">Loading...</div></div>;
  }

  return (
    <div className="space-y-6" data-testid="financial-reports-page">
      <div>
        <h1 className="text-3xl font-heading font-semibold text-foreground mb-2">Financial Reports</h1>
        <p className="text-muted-foreground">Comprehensive accounting and financial statements</p>
      </div>

      {/* Tab Navigation */}
      <div className="bg-white p-1 rounded-xl border border-border shadow-sm inline-flex">
        <button
          onClick={() => setActiveTab('income')}
          className={`px-6 py-2 rounded-lg text-sm font-medium transition-colors ${
            activeTab === 'income' ? 'bg-primary text-white' : 'text-muted-foreground hover:text-foreground'
          }`}
          data-testid="income-statement-tab"
        >
          Income Statement
        </button>
        <button
          onClick={() => setActiveTab('balance')}
          className={`px-6 py-2 rounded-lg text-sm font-medium transition-colors ${
            activeTab === 'balance' ? 'bg-primary text-white' : 'text-muted-foreground hover:text-foreground'
          }`}
          data-testid="balance-sheet-tab"
        >
          Balance Sheet
        </button>
        <button
          onClick={() => setActiveTab('cashflow')}
          className={`px-6 py-2 rounded-lg text-sm font-medium transition-colors ${
            activeTab === 'cashflow' ? 'bg-primary text-white' : 'text-muted-foreground hover:text-foreground'
          }`}
          data-testid="cash-flow-tab"
        >
          Cash Flow
        </button>
      </div>

      {/* Income Statement */}
      {activeTab === 'income' && incomeStatement && (
        <div className="bg-white rounded-xl border border-border shadow-sm p-8" data-testid="income-statement">
          <div className="flex items-center space-x-3 mb-8">
            <div className="w-12 h-12 bg-green-50 rounded-xl flex items-center justify-center">
              <TrendingUp className="w-6 h-6 text-green-600" />
            </div>
            <div>
              <h2 className="text-2xl font-heading font-semibold text-foreground">Income Statement</h2>
              <p className="text-sm text-muted-foreground">Profit & Loss Report</p>
            </div>
          </div>

          <div className="space-y-6">
            {/* Revenue Section */}
            <div className="border-b border-border pb-4">
              <h3 className="text-lg font-semibold text-foreground mb-3">Revenue</h3>
              <div className="flex justify-between items-center py-2">
                <span className="text-muted-foreground">Total Sales</span>
                <span className="font-semibold text-foreground">RWF {incomeStatement.revenue.total_sales.toFixed(2)}</span>
              </div>
            </div>

            {/* COGS Section */}
            <div className="border-b border-border pb-4">
              <div className="flex justify-between items-center py-2">
                <span className="font-medium text-foreground">Cost of Goods Sold</span>
                <span className="font-semibold text-red-600">RWF {incomeStatement.cost_of_goods_sold.toFixed(2)}</span>
              </div>
              <div className="flex justify-between items-center py-2 bg-green-50 px-3 rounded-lg mt-2">
                <span className="font-semibold text-foreground">Gross Profit</span>
                <span className="font-bold text-green-600">RWF {incomeStatement.gross_profit.toFixed(2)}</span>
              </div>
            </div>

            {/* Operating Expenses */}
            <div className="border-b border-border pb-4">
              <h3 className="text-lg font-semibold text-foreground mb-3">Operating Expenses</h3>
              {Object.entries(incomeStatement.operating_expenses).map(([category, amount]) => (
                <div key={category} className="flex justify-between items-center py-2">
                  <span className="text-muted-foreground">{category}</span>
                  <span className="text-foreground">RWF {amount.toFixed(2)}</span>
                </div>
              ))}
              <div className="flex justify-between items-center py-2 mt-2 font-medium">
                <span className="text-foreground">Total Operating Expenses</span>
                <span className="text-red-600">RWF {incomeStatement.total_operating_expenses.toFixed(2)}</span>
              </div>
            </div>

            {/* Net Profit */}
            <div className="bg-primary/10 p-6 rounded-xl">
              <div className="flex justify-between items-center mb-2">
                <span className="text-xl font-heading font-semibold text-foreground">Net Profit</span>
                <span className={`text-2xl font-bold ${incomeStatement.net_profit >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                  RWF {incomeStatement.net_profit.toFixed(2)}
                </span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm text-muted-foreground">Profit Margin</span>
                <span className="text-sm font-semibold text-foreground">{incomeStatement.profit_margin.toFixed(2)}%</span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Balance Sheet */}
      {activeTab === 'balance' && balanceSheet && (
        <div className="bg-white rounded-xl border border-border shadow-sm p-8" data-testid="balance-sheet">
          <div className="flex items-center space-x-3 mb-8">
            <div className="w-12 h-12 bg-blue-50 rounded-xl flex items-center justify-center">
              <Package className="w-6 h-6 text-blue-600" />
            </div>
            <div>
              <h2 className="text-2xl font-heading font-semibold text-foreground">Balance Sheet</h2>
              <p className="text-sm text-muted-foreground">Assets, Liabilities & Equity</p>
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {/* Assets */}
            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-foreground border-b border-border pb-2">Assets</h3>
              <div className="space-y-2">
                <div className="flex justify-between py-2">
                  <span className="text-muted-foreground">Cash</span>
                  <span className="font-medium text-foreground">RWF {balanceSheet.assets.current_assets.cash.toFixed(2)}</span>
                </div>
                <div className="flex justify-between py-2">
                  <span className="text-muted-foreground">Accounts Receivable</span>
                  <span className="font-medium text-foreground">RWF {balanceSheet.assets.current_assets.accounts_receivable.toFixed(2)}</span>
                </div>
                <div className="flex justify-between py-2">
                  <span className="text-muted-foreground">Inventory</span>
                  <span className="font-medium text-foreground">RWF {balanceSheet.assets.current_assets.inventory.toFixed(2)}</span>
                </div>
                <div className="flex justify-between py-3 bg-blue-50 px-3 rounded-lg font-semibold">
                  <span className="text-foreground">Total Assets</span>
                  <span className="text-blue-600">RWF {balanceSheet.assets.total_assets.toFixed(2)}</span>
                </div>
              </div>
            </div>

            {/* Liabilities & Equity */}
            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-semibold text-foreground border-b border-border pb-2 mb-4">Liabilities</h3>
                <div className="space-y-2">
                  <div className="flex justify-between py-2">
                    <span className="text-muted-foreground">Accounts Payable</span>
                    <span className="font-medium text-foreground">RWF {balanceSheet.liabilities.current_liabilities.accounts_payable.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between py-3 bg-red-50 px-3 rounded-lg font-semibold">
                    <span className="text-foreground">Total Liabilities</span>
                    <span className="text-red-600">RWF {balanceSheet.liabilities.total_liabilities.toFixed(2)}</span>
                  </div>
                </div>
              </div>

              <div>
                <h3 className="text-lg font-semibold text-foreground border-b border-border pb-2 mb-4">Equity</h3>
                <div className="flex justify-between py-3 bg-primary/10 px-3 rounded-lg font-semibold">
                  <span className="text-foreground">Total Equity</span>
                  <span className="text-primary">RWF {balanceSheet.equity.total_equity.toFixed(2)}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Cash Flow Statement */}
      {activeTab === 'cashflow' && cashFlow && (
        <div className="bg-white rounded-xl border border-border shadow-sm p-8" data-testid="cash-flow">
          <div className="flex items-center space-x-3 mb-8">
            <div className="w-12 h-12 bg-purple-50 rounded-xl flex items-center justify-center">
              <Activity className="w-6 h-6 text-purple-600" />
            </div>
            <div>
              <h2 className="text-2xl font-heading font-semibold text-foreground">Cash Flow Statement</h2>
              <p className="text-sm text-muted-foreground">Cash inflows and outflows</p>
            </div>
          </div>

          <div className="space-y-6">
            {/* Operating Activities */}
            <div className="border-b border-border pb-4">
              <h3 className="text-lg font-semibold text-foreground mb-3">Operating Activities</h3>
              <div className="space-y-2">
                <div className="flex justify-between py-2">
                  <span className="text-muted-foreground">Cash from Sales</span>
                  <span className="font-medium text-green-600">RWF {cashFlow.operating_activities.cash_from_sales.toFixed(2)}</span>
                </div>
                <div className="flex justify-between py-2">
                  <span className="text-muted-foreground">Cash for Purchases</span>
                  <span className="font-medium text-red-600">RWF {cashFlow.operating_activities.cash_for_purchases.toFixed(2)}</span>
                </div>
                <div className="flex justify-between py-2">
                  <span className="text-muted-foreground">Cash for Expenses</span>
                  <span className="font-medium text-red-600">RWF {cashFlow.operating_activities.cash_for_expenses.toFixed(2)}</span>
                </div>
                <div className="flex justify-between py-3 bg-purple-50 px-3 rounded-lg font-semibold mt-2">
                  <span className="text-foreground">Net Cash from Operations</span>
                  <span className={cashFlow.operating_activities.net_cash_from_operations >= 0 ? 'text-green-600' : 'text-red-600'}>
                    RWF {cashFlow.operating_activities.net_cash_from_operations.toFixed(2)}
                  </span>
                </div>
              </div>
            </div>

            {/* Payments by Method */}
            <div className="border-b border-border pb-4">
              <h3 className="text-lg font-semibold text-foreground mb-3">Payments Received by Method</h3>
              {Object.entries(cashFlow.payments_by_method).map(([method, amount]) => (
                <div key={method} className="flex justify-between py-2">
                  <span className="text-muted-foreground">{method}</span>
                  <span className="font-medium text-foreground">RWF {amount.toFixed(2)}</span>
                </div>
              ))}
            </div>

            {/* Net Increase in Cash */}
            <div className="bg-primary/10 p-6 rounded-xl">
              <div className="flex justify-between items-center">
                <span className="text-xl font-heading font-semibold text-foreground">Net Increase in Cash</span>
                <span className={`text-2xl font-bold ${cashFlow.net_increase_in_cash >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                  RWF {cashFlow.net_increase_in_cash.toFixed(2)}
                </span>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FinancialReportsPage;
